import {
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Tooltip } from "@fluentui/react-components";
import { Alert16Regular } from "@fluentui/react-icons";

const cx = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(" ");

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: "primary" | "default" | "ghost" | "danger";
  size?: "md" | "lg";
  type?: "button" | "submit";
  /** Why the button is unavailable (missing permission, read-only tenant…): shown as a tooltip and exposed with aria-disabled (04 §7). */
  disabledReason?: string;
  /** Forces the busy state (spinner, no click). Usually not needed: a button that starts a request is busy by itself. */
  loading?: boolean;
}

/**
 * Global rule: a button whose action waits for the backend shows a spinner and ignores further clicks until the action is done.
 * It needs no wiring: while a click handler runs, the requests it starts (a mutation of the app's query client, bound with
 * `bindMutationCache`, or a promise the handler returns) are attributed to that button.
 */
let clicking: { track: (done: Promise<unknown>) => void } | null = null;

/** Attributes a pending action to the button being clicked, if any (called synchronously from the click handler). */
export function trackButtonBusy(done: Promise<unknown>): void {
  clicking?.track(done);
}

interface MutationLike {
  state: { status: string };
}
interface MutationCacheLike {
  subscribe(cb: (event: { type: string; mutation?: MutationLike }) => void): () => void;
}

/** Binds the app's mutation cache: every mutation started by a click keeps that button busy until it settles. */
export function bindMutationCache(cache: MutationCacheLike): void {
  cache.subscribe((event) => {
    if (event.type !== "added" || !event.mutation || !clicking) return;
    const m = event.mutation;
    trackButtonBusy(
      new Promise<void>((resolve) => {
        const off = cache.subscribe((e) => {
          if (e.mutation === m && (m.state.status === "success" || m.state.status === "error")) {
            off();
            resolve();
          }
        });
      }),
    );
  });
}

/** One primary per surface. A disabled button always says why (tooltip) and stays focusable. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "default",
    size = "md",
    type = "button",
    disabledReason,
    disabled,
    loading,
    className,
    onClick,
    children,
    ...rest
  },
  ref,
) {
  const [pending, setPending] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const busy = Boolean(loading) || pending > 0;
  const blocked = Boolean(disabledReason) || disabled || busy;
  const click = (e: MouseEvent<HTMLButtonElement>) => {
    if (blocked) {
      e.preventDefault();
      return;
    }
    const outer = clicking;
    clicking = {
      track: (done) => {
        setPending((n) => n + 1);
        const end = () => mounted.current && setPending((n) => Math.max(0, n - 1));
        done.then(end, end);
      },
    };
    try {
      const result = onClick?.(e) as unknown;
      if (result && typeof (result as Promise<unknown>).then === "function")
        clicking.track(result as Promise<unknown>);
    } finally {
      clicking = outer;
    }
  };
  const button = (
    <button
      ref={ref}
      type={type}
      className={cx(
        "gp-btn",
        variant !== "default" && `gp-btn--${variant}`,
        size === "lg" && "gp-btn--lg",
        busy && "gp-btn--loading",
        className,
      )}
      aria-disabled={blocked ? true : undefined}
      aria-busy={busy ? true : undefined}
      onClick={click}
      {...rest}
    >
      {busy ? <span className="gp-spinner gp-btn-spinner" aria-hidden /> : null}
      {children}
    </button>
  );
  return disabledReason ? (
    <Tooltip content={disabledReason} relationship="description">
      {button}
    </Tooltip>
  ) : (
    button
  );
});

export function LinkButton({
  href,
  children,
  variant = "ghost",
  onNavigate,
}: {
  href: string;
  children: ReactNode;
  variant?: "primary" | "default" | "ghost";
  onNavigate?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <a
      href={href}
      className={cx("gp-btn", variant !== "default" && `gp-btn--${variant}`)}
      onClick={onNavigate}
    >
      {children}
    </a>
  );
}

export interface FieldProps {
  label: string;
  help?: string | undefined;
  error?: string | undefined;
  children: ReactElement;
}

/** Label above, helper below, error linked with aria-describedby (04 Input). */
export function Field({ label, help, error, children }: FieldProps) {
  const id = useId();
  const describedBy =
    [help ? `${id}-help` : null, error ? `${id}-err` : null].filter(Boolean).join(" ") || undefined;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })
    : children;
  return (
    <div className="gp-field">
      <label htmlFor={id} className="t-body-strong">
        {label}
      </label>
      {control}
      {help ? (
        <span id={`${id}-help`} className="gp-help t-body-sm">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-err`} className="gp-error t-body-sm" role="alert">
          <Alert16Regular aria-hidden />
          {error}
        </span>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cx("gp-input", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx("gp-textarea", className)} {...rest} />;
  },
);

export interface Option {
  value: string;
  label: string;
}

export function SelectX({
  options,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { options: Option[] }) {
  return (
    <select className="gp-select" {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  pending,
  pendingLabel,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  pending?: boolean;
  pendingLabel?: string;
}) {
  return (
    <label className="gp-toggle">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="gp-toggle-track" aria-hidden />
      <span className="t-body">{label}</span>
      {pending ? (
        <span role="status" className="gp-row t-body-sm gp-muted">
          <span className="gp-spinner" aria-hidden />
          {pendingLabel}
        </span>
      ) : null}
    </label>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  return (
    <label className="gp-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="t-body">{label}</span>
      {hint ? <span className="t-body-sm gp-faint">{hint}</span> : null}
    </label>
  );
}

/** Single-select pill group (format pickers). */
export function PillGroup({
  legend,
  name,
  value,
  options,
  onChange,
}: {
  legend: string;
  name: string;
  value: string;
  options: Option[];
  onChange: (v: string) => void;
}) {
  return (
    <fieldset className="gp-pillgroup">
      <legend className="gp-sr-only">{legend}</legend>
      {options.map((o) => (
        <label key={o.value}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          <span className="t-body">{o.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** Label / description on the left, control on the right (Settings rows). */
export function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="gp-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
      <div className="gp-stack" style={{ gap: "var(--space-2)", flex: 1, minWidth: 0 }}>
        <span className="t-body-strong">{label}</span>
        {description ? <span className="t-body-sm gp-faint">{description}</span> : null}
      </div>
      <div style={{ flex: "0 1 auto" }}>{children}</div>
    </div>
  );
}
